import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { distinctUntilChanged, tap, switchMap, finalize, catchError, take, filter, map } from 'rxjs/operators';

import { Validator } from '@vendasta/shared';
import { Country, State } from '@vendasta/country-state';
import { Merchant, MerchantService } from '@galaxy/billing';
import { SubsidiaryWarningDialogComponent } from './subsidiary-warning-dialog/subsidiary-warning-dialog.component';
import { AddressAPIService } from '@vendasta/address';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateService } from '@ngx-translate/core';

interface ContactInfoData {
  merchantId: string;
  merchant?: Merchant;
}

const LOWER_US_COUNTRY_CODE = 'us';

@Component({
  selector: 'billing-ui-edit-contact-info-dialog',
  templateUrl: 'edit-contact-info-dialog.component.html',
  styleUrls: ['edit-contact-info-dialog.component.scss'],
})
export class EditContactInfoDialogComponent implements OnInit, OnDestroy {
  public merchantId: string;
  public merchant: Merchant | null;
  public formGroup: UntypedFormGroup;
  public countries$: Observable<Country[]>;
  public states$$: BehaviorSubject<State[]> = new BehaviorSubject<State[]>([]);
  public isSaving$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showSubsidiaryChangeWarning$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  stateRequired = true;

  private subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<EditContactInfoDialogComponent, boolean>,
    private formBuilder: UntypedFormBuilder,
    private addressService: AddressAPIService,
    private merchantService: MerchantService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) private data: ContactInfoData,
  ) {
    this.merchantId = this.data.merchantId;
    this.merchant = this.data.merchant || null;
    this.countries$ = this.addressService.listAllCountryOptions().pipe(
      map((countries) => countries.slice().sort((a, b) => a.name.localeCompare(b.name))), // sort a copy alphabetically
    );

    this.formGroup = this.formBuilder.group({
      contactName: ['', Validators.required],
      companyName: ['', Validators.required],
      emailAddresses: ['', [Validators.required, Validator.isEmailList]],
      phoneNumber: ['', [Validators.required, Validator.isPhoneNumber]],
      address: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      country: ['', Validators.required],
      postalCode: ['', Validators.required],
    });
  }

  get country(): UntypedFormControl {
    return this.formGroup.get('country') as UntypedFormControl;
  }

  get state(): UntypedFormControl {
    return this.formGroup.get('state') as UntypedFormControl;
  }

  get emailAddresses(): UntypedFormControl {
    return this.formGroup.get('emailAddresses') as UntypedFormControl;
  }

  get postalCode(): UntypedFormControl {
    return this.formGroup.get('postalCode') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.country.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap(() => this.state.patchValue('')), // reset state selection if country changes
          tap((countryCode) =>
            this.showSubsidiaryChangeWarning$$.next(
              this.wouldChangeSubsidiary(countryCode, this.merchant?.country || ''),
            ),
          ),
          filter((countryCode) => !!countryCode),
          switchMap((countryCode) => {
            const locale = this.translateService.currentLang || this.translateService.defaultLang;
            return this.addressService.getCountryConfiguration(countryCode, locale);
          }),
        )
        .subscribe((config) => {
          const states = config?.zones || [];
          this.states$$.next(states);
          if (states.length > 0) {
            this.stateRequired = true;
            this.state.setValidators(Validators.required);
          } else {
            this.stateRequired = false;
            this.state.clearValidators();
          }
          if (this.merchant && states.find((s) => s.code === this.merchant.state)) {
            this.state.patchValue(this.merchant.state);
          }

          this.postalCode.setValidators([Validators.required, Validator.isValidPostalCode(config?.code)]);

          this.postalCode.updateValueAndValidity();
          this.state.updateValueAndValidity();
          this.country.updateValueAndValidity();
        }),
    );

    this.setContactInfo();
  }

  /**
   * Determines whether or not the merchant would change billing subsidiaries based on their country seleection
   *
   * @param newCountryCode - The two letter country code of a selected country
   * @param currentCountryCode - The current country code of the merchant
   * @returns {boolean} - True if the merchant would change subsidiaries as a result of the selected country; else false
   */
  private wouldChangeSubsidiary(newCountryCode: string, currentCountryCode: string): boolean {
    switch (currentCountryCode.toLowerCase()) {
      case LOWER_US_COUNTRY_CODE:
        return newCountryCode.toLowerCase() !== LOWER_US_COUNTRY_CODE;
      default:
        return newCountryCode.toLowerCase() === LOWER_US_COUNTRY_CODE;
    }
  }

  private setContactInfo(): void {
    if (!this.merchant) {
      return;
    }

    let concatenatedEmails: string = this.merchant.emailAddress || '';
    const emailAddresses: string[] = this.merchant.additionalEmailAddresses
      ? new Array(...this.merchant.additionalEmailAddresses)
      : [];
    if (emailAddresses && emailAddresses.length > 0) {
      if (this.merchant.emailAddress) {
        emailAddresses.unshift(this.merchant.emailAddress);
      }
      concatenatedEmails = emailAddresses.join(', ');
    }

    this.formGroup.setValue({
      contactName: this.merchant.contactName || '',
      companyName: this.merchant.companyName || '',
      emailAddresses: concatenatedEmails,
      phoneNumber: this.merchant.phoneNumber || '',
      address: this.merchant.address || '',
      city: this.merchant.city || '',
      state: this.merchant.state || '',
      country: this.merchant.country || '',
      postalCode: this.merchant.zipCode || '',
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onSave(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.isSaving$$.next(true);
    const { contactName, companyName, emailAddresses, phoneNumber, address, city, state, country, postalCode } =
      this.formGroup.getRawValue();

    const emailList: string[] = emailAddresses.split(/[,;]+/).map((email) => email.trim());
    const primaryEmail: string = emailList.shift(); // remove first email address from list and use it as as the primary email

    let resp$: Observable<boolean>;
    if (this.merchant) {
      resp$ = this.merchantService.update(
        this.merchantId,
        address,
        city,
        state,
        country,
        postalCode,
        primaryEmail,
        phoneNumber,
        contactName,
        companyName,
        emailList,
      );
    } else {
      resp$ = this.merchantService.create(
        this.merchantId,
        address,
        city,
        state,
        country,
        postalCode,
        primaryEmail,
        phoneNumber,
        contactName,
        companyName,
        emailList,
      );
    }

    resp$
      .pipe(
        take(1),
        tap(() => {
          this.snackbarService.openSuccessSnack('BILLING_CONTACT.SAVE_SUCCESS');
          this.onClose(true);
        }),
        catchError(() => {
          this.snackbarService.openErrorSnack('BILLING_CONTACT.SAVE_ERROR');
          return of(null);
        }),
        finalize(() => this.isSaving$$.next(false)),
      )
      .subscribe();
  }

  public onClose(wasUpdated: boolean): void {
    this.dialogRef.close(wasUpdated);
    this.showSubsidiaryChangeWarning$$.pipe(take(1)).subscribe((showWarning) => {
      if (showWarning) {
        this.dialog.open(SubsidiaryWarningDialogComponent);
      }
    });
  }
}
